import { Marked, marked } from 'marked';
import DOMPurify from 'dompurify';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import python from 'highlight.js/lib/languages/python';
import css from 'highlight.js/lib/languages/css';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
import xml from 'highlight.js/lib/languages/xml';
import sql from 'highlight.js/lib/languages/sql';
import markdown from 'highlight.js/lib/languages/markdown';

hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('js', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('ts', typescript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('py', python);
hljs.registerLanguage('css', css);
hljs.registerLanguage('json', json);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('sh', bash);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('markdown', markdown);
hljs.registerLanguage('md', markdown);

marked.use({
  breaks: true,
  gfm: true,
  renderer: {
    code({ text, lang }: { text: string; lang?: string }) {
      const language = lang && hljs.getLanguage(lang) ? lang : undefined;
      const highlighted = language
        ? hljs.highlight(text, { language }).value
        : escapeHtml(text);
      const langLabel = language ? `<span class="code-lang">${escapeHtml(language)}</span>` : '';
      return `<pre><code class="hljs${language ? ` language-${escapeHtml(language)}` : ''}">${langLabel}${highlighted}</code></pre>`;
    },
  },
});

export function renderMarkdown(text: string, mentionedUserIds?: string[], userMap?: Map<string, string>): string {
  let processed = text;

  processed = processed.replace(/\\\n/g, '\n');

  // Unescape fenced code block delimiters that prosemirror-markdown escapes
  // when the user types backticks as plain text instead of using a codeBlock node
  processed = processed.replace(/^\\`\\`\\`(\w*)$/gm, '```$1');

  if (userMap) {
    processed = processed.replace(/<@([^>]+)>/g, (_match, userId: string) => {
      // The raw id lives only in data-mention-id; the text node carries the
      // display name, or a truncated id when the member list has not loaded, so
      // no full id leaks into DOM textContent.
      const displayName = userMap.get(userId);
      const safeId = escapeHtml(userId);
      if (displayName) return `<span class="mention" data-mention-id="${safeId}">@${escapeHtml(displayName)}</span>`;
      const shortId = userId.length > 8 ? userId.slice(0, 8) : userId;
      return `<span class="mention" data-mention-id="${safeId}">@${escapeHtml(shortId)}</span>`;
    });
  }

  if (mentionedUserIds && mentionedUserIds.length > 0 && userMap) {
    for (const userId of mentionedUserIds) {
      const displayName = userMap.get(userId);
      if (displayName) {
        const regex = new RegExp(`@${escapeRegex(displayName)}(?![^<]*<\\/span>)`, 'g');
        // A replacer function, not a replacement string: `$&` and `` $` `` in a
        // display name would otherwise splice in the surrounding message.
        processed = processed.replace(
          regex,
          () => `<span class="mention">@${escapeHtml(displayName)}</span>`,
        );
      }
    }
  }

  const rawHtml = marked.parse(processed) as string;

  const clean = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'blockquote',
      'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      // An agent folds its reasoning away behind these rather than letting it
      // compete with the answer; without them the reader sees the tags as text.
      'details', 'summary',
      'span', 'div', 'img',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'src', 'alt', 'title', 'data-mention-id'],
  });

  return clean.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
}

const fileMarkdown = new Marked({
  breaks: true,
  gfm: true,
  renderer: {
    html({ text }: { text: string }) {
      return escapeHtml(text);
    },
    image({ text }: { text: string }) {
      return escapeHtml(text);
    },
    code({ text, lang }: { text: string; lang?: string }) {
      const language = lang && hljs.getLanguage(lang) ? lang : undefined;
      const highlighted = language
        ? hljs.highlight(text, { language }).value
        : escapeHtml(text);
      const langLabel = language ? `<span class="code-lang">${escapeHtml(language)}</span>` : '';
      return `<pre><code class="hljs${language ? ` language-${escapeHtml(language)}` : ''}">${langLabel}${highlighted}</code></pre>`;
    },
  },
});

export function renderFileMarkdown(text: string): string {
  const rawHtml = fileMarkdown.parse(text) as string;
  const clean = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'blockquote',
      'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'span',
    ],
    ALLOWED_ATTR: ['href', 'class', 'title'],
  });
  return clean.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
